import type { TFunction } from 'i18next';

/**
 * A readable sentence for a Microsoft Graph error code, or `null` when we have
 * nothing honest to say about it.
 *
 * Graph's `message` is not translatable material: the one that motivated this
 * reads "Exception of type
 * 'Microsoft.People.Image.Common.Exceptions.ProfileBadRequestException' was
 * thrown." — a .NET `ToString()` whose only informative part is the code beside
 * it. Microsoft's error reference says as much the other way round: branch on
 * `code`, never on `message`. So the codes are what get translated, one at a
 * time, as operators actually meet them.
 *
 * Returning `null` rather than a generic sentence is deliberate. An unknown
 * code keeps the raw message visible on its own, which is the string worth
 * pasting into a ticket; inventing prose over it would hide the only evidence
 * there is. No library can fill this in — Microsoft publishes no catalogue of
 * these codes, so no table of them can be complete.
 */
export function graphErrorMessage(
  t: TFunction,
  code: string | undefined,
): string | null {
  if (!code) {
    return null;
  }
  const key = `graph.error.${code}`;
  const translated = t(key, { defaultValue: '' });
  return translated === '' ? null : translated;
}
